import { useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  Zap,
  RefreshCw,
  BarChart3,
  Settings,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Target,
  Globe,
  GitBranch,
  ListTodo,
  History,
  Shield,
  UserCheck,
  Calendar,
  SlidersHorizontal,
  ClipboardCheck,
  Database,
  FileSpreadsheet,
  Scan,
  ImagePlay,
  Tags,
} from "lucide-react";
import { useAppStore } from "../../stores/appStore";

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  children?: NavItem[];
}

// Preload map for lazy-loaded pages
const preloadMap: Record<string, () => Promise<unknown>> = {
  "/dashboard": () => import("../../pages/Dashboard"),
  "/pipeline": () => import("../../pages/Pipeline"),
  "/athletes": () => import("../../pages/Athletes"),
  "/colleges": () => import("../../pages/Colleges"),
  "/matches": () => import("../../pages/Matches"),
  "/matching": () => import("../../pages/MatchingEngine"),
  "/jobs": () => import("../../pages/Jobs"),
  "/jobs/history": () => import("../../pages/Jobs"),
  "/quality": () => import("../../pages/DataQuality"),
  "/quality/athletes": () => import("../../pages/DataQuality"),
  "/schedules": () => import("../../pages/Schedules"),
  "/scraper": () => import("../../pages/Scraper"),
  "/scraper/sources": () => import("../../pages/ScraperConfig"),
  "/scraper/review": () => import("../../pages/ScraperReview"),
  "/media-crawler": () => import("../../pages/MediaCrawler"),
  "/media-approval": () => import("../../pages/MediaApproval"),
  "/scraper/resolution-cache": () => import("../../pages/ResolutionCache"),
  "/data-ingestion/team-aliases": () => import("../../pages/DataIngestion/DataIngestionTeamAliases"),
  "/sync": () => import("../../pages/DataSync"),
  "/analytics": () => import("../../pages/Analytics"),
  "/system": () => import("../../pages/System"),
};

// Navigation organized into logical groups
const navigationGroups = [
  {
    name: "Operations",
    items: [
      { name: "Pipeline", href: "/pipeline", icon: GitBranch },
      { name: "Job Queue", href: "/jobs", icon: ListTodo },
      { name: "Job History", href: "/jobs/history", icon: History },
      { name: "Schedules", href: "/schedules", icon: Calendar },
    ],
  },
  {
    name: "Data Quality",
    items: [
      { name: "Quality Overview", href: "/quality", icon: Shield },
      { name: "Athlete Quality", href: "/quality/athletes", icon: UserCheck },
    ],
  },
  {
    name: "Data",
    items: [
      { name: "Athletes", href: "/athletes", icon: Users },
      { name: "Colleges", href: "/colleges", icon: GraduationCap },
      { name: "Matches", href: "/matches", icon: Target },
    ],
  },
  {
    name: "Tools",
    items: [
      { name: "Matching Engine", href: "/matching", icon: Zap },
      {
        name: "Scraper",
        href: "/scraper",
        icon: Globe,
        children: [
          { name: "Config", href: "/scraper/sources", icon: SlidersHorizontal },
          { name: "Resolution Cache", href: "/scraper/resolution-cache", icon: Database },
        ],
      },
      { name: "Attribute Review", href: "/scraper/review", icon: ClipboardCheck },
      { name: "Media Crawler", href: "/media-crawler", icon: Scan },
      { name: "Media Approval", href: "/media-approval", icon: ImagePlay },
      {
        name: "Data Ingestion",
        href: "/data-ingestion/sources",
        icon: FileSpreadsheet,
        children: [
          { name: "Cache", href: "/data-ingestion/cache", icon: Database },
          { name: "Team Aliases", href: "/data-ingestion/team-aliases", icon: Tags },
          { name: "College Lookups", href: "/data-ingestion/college-lookups", icon: Database },
        ],
      },
      { name: "Data Sync", href: "/sync", icon: RefreshCw },
    ] as NavItem[],
  },
  {
    name: "System",
    items: [
      { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
      { name: "Analytics", href: "/analytics", icon: BarChart3 },
      { name: "Settings", href: "/system", icon: Settings },
    ],
  },
];

export default function Sidebar() {
  const location = useLocation();
  const { sidebarCollapsed, setSidebarCollapsed } = useAppStore();

  // Preload page on hover for faster navigation
  const handlePreload = useCallback((href: string) => {
    const preloader = preloadMap[href];
    if (preloader) {
      preloader();
    }
  }, []);

  return (
    <div
      className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ${
        sidebarCollapsed ? "w-16" : "w-64"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
        {!sidebarCollapsed && (
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">SF</span>
            </div>
            <span className="text-lg font-semibold text-gray-900 dark:text-white">
              SportsForce
            </span>
          </div>
        )}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          {sidebarCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <ChevronLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navigationGroups.map((group, groupIndex) => (
          <div key={group.name} className={groupIndex > 0 ? "mt-4" : ""}>
            {/* Group Header - only show when not collapsed */}
            {!sidebarCollapsed && (
              <div className="px-3 mb-2">
                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  {group.name}
                </span>
              </div>
            )}
            {/* Group Items */}
            <div className="space-y-1">
              {group.items.map((item) => {
                const isActive =
                  location.pathname === item.href ||
                  (item.href === "/pipeline" && location.pathname === "/");
                const hasChildren = !!item.children?.length;
                const childActive = hasChildren
                  ? item.children!.some((c) => location.pathname === c.href)
                  : false;
                const showChildren =
                  hasChildren && !sidebarCollapsed && (isActive || childActive);

                return (
                  <div key={item.name}>
                    <Link
                      to={item.href}
                      onMouseEnter={() => handlePreload(item.href)}
                      className={`sidebar-nav-item ${
                        isActive || (hasChildren && childActive)
                          ? "sidebar-nav-item-active"
                          : "sidebar-nav-item-inactive"
                      } ${
                        sidebarCollapsed ? "justify-center px-2" : "justify-start px-4"
                      }`}
                      title={sidebarCollapsed ? item.name : undefined}
                    >
                      <item.icon
                        className={`w-5 h-5 ${sidebarCollapsed ? "" : "mr-3"}`}
                      />
                      {!sidebarCollapsed && (
                        <span className="text-sm font-medium flex-1">{item.name}</span>
                      )}
                      {!sidebarCollapsed && hasChildren && (
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            showChildren ? "" : "-rotate-90"
                          }`}
                        />
                      )}
                    </Link>
                    {showChildren && (
                      <div className="mt-1 ml-4 pl-3 border-l border-gray-200 dark:border-gray-700 space-y-1">
                        {item.children!.map((child) => {
                          const childIsActive = location.pathname === child.href;
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              onMouseEnter={() => handlePreload(child.href)}
                              className={`sidebar-nav-item justify-start px-3 ${
                                childIsActive
                                  ? "sidebar-nav-item-active"
                                  : "sidebar-nav-item-inactive"
                              }`}
                            >
                              <child.icon className="w-4 h-4 mr-2" />
                              <span className="text-sm">{child.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* External Links */}
        <div className="pt-2 mt-4 border-t border-gray-200 dark:border-gray-700">
          <a
            href={`${
              import.meta.env.VITE_API_URL || ""
            }/api-docs`}
            target="_blank"
            rel="noopener noreferrer"
            className={`sidebar-nav-item sidebar-nav-item-inactive ${
              sidebarCollapsed ? "justify-center px-2" : "justify-start px-4"
            }`}
            title={sidebarCollapsed ? "API Documentation" : undefined}
          >
            <ExternalLink
              className={`w-5 h-5 ${sidebarCollapsed ? "" : "mr-3"}`}
            />
            {!sidebarCollapsed && (
              <span className="text-sm font-medium">API Docs</span>
            )}
          </a>
        </div>
      </nav>

      {/* Footer */}
      {!sidebarCollapsed && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Admin Dashboard v1.0.0
          </div>
        </div>
      )}
    </div>
  );
}
